import React, { useEffect, useRef, useState } from "react";
import { setRuntimeAuthToken } from "@/api/client";
import CookieBanner from "@/components/CookieBanner";

/* ------------------------------------------------------------------ */
/*  Facebook Pixel                                                      */
/* ------------------------------------------------------------------ */

declare global {
  interface Window {
    fbq: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[]; loaded: boolean; version: string };
    _fbq?: typeof window.fbq;
  }
}

// Production landing (artistascend.app) links to the app host; the local Vite
// dev server stays on its own origin (dev must never jump to production).
const SIGN_IN_URL = import.meta.env.DEV ? "/login" : "https://agents.artistascend.app/login";
const FB_PIXEL_ID = (import.meta.env.VITE_FACEBOOK_PIXEL_ID as string | undefined) ?? "";

function initFBPixel(): void {
  if (!FB_PIXEL_ID || typeof window === "undefined") return;
  if (window.fbq) return; // already initialised

  // Standard Facebook Pixel base code (SDK handles queue until script loads)
  const fbq: typeof window.fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
}

function fbTrack(event: string, params?: Record<string, unknown>): void {
  if (!FB_PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", event, params ?? {});
}

/**
 * Re-init with Advanced Matching after we know the user's email.
 * Facebook hashes PII client-side before sending — safe to pass plaintext.
 */
function fbInitWithEmail(email: string): void {
  if (!FB_PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  window.fbq("init", FB_PIXEL_ID, { em: email.toLowerCase().trim() });
}
import {
  Compass,
  Mail,
  Route,
  CalendarRange,
  Megaphone,
  Radar,
  Receipt,
  Workflow,
  Brain,
  Activity,
  ShieldCheck,
  Users,
  CheckCircle,
  Clock,
  Moon,
  Sun,
  Music,
  Headphones,
  Mic,
} from "lucide-react";
import favicon from "../assets/branding/aafavicon.png";

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

function useCountUp(target: number, duration = 1200): number {
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);

  return value;
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const featureGroups = [
  {
    slug: "ops",
    title: "Core operations",
    items: [
      {
        icon: Users,
        title: "Every contact in one place",
        body: "Keep every venue contact, conversation, and deal in one place \u2014 no duplicates, no digging through DMs.",
      },
      {
        icon: Compass,
        title: "Fast ingest + enrichment",
        body: "Drop in a spreadsheet, forward an email, or paste a list \u2014 your contacts are organized in seconds.",
      },
      {
        icon: Mail,
        title: "Email that books shows",
        body: "Draft and send personalized booking emails in minutes instead of hours. Every send needs your approval.",
      },
      {
        icon: Route,
        title: "Booking pipeline that closes",
        body: "See exactly where every venue stands \u2014 who you\u2019re waiting on, who\u2019s ready to confirm, what\u2019s next.",
      },
    ],
  },
  {
    slug: "growth",
    title: "Tour + growth",
    items: [
      {
        icon: CalendarRange,
        title: "Smarter tour routing",
        body: "Plan smarter routes that save gas money and open up better back-to-back dates.",
      },
      {
        icon: Megaphone,
        title: "Marketing + press in one place",
        body: "Get press kits, social posts, and release announcements drafted and queued without switching tools.",
      },
      {
        icon: Radar,
        title: "Know where your fans are",
        body: "See which songs are growing, which markets are hot, and where your fans actually are.",
      },
      {
        icon: Receipt,
        title: "Know what each show earned",
        body: "Know exactly what each show earned after expenses \u2014 no more guessing from bank statements.",
      },
    ],
  },
  {
    slug: "scale",
    title: "Scale + governance",
    items: [
      {
        icon: Workflow,
        title: "Nothing ships without your sign-off",
        body: "Nothing goes out to a venue or press contact without your approval. You stay in control.",
      },
      {
        icon: Brain,
        title: "Pick up right where you left off",
        body: "Every conversation, note, and deal history stays attached to the contact \u2014 pick up right where you left off.",
      },
      {
        icon: Activity,
        title: "Autopilot with full visibility",
        body: "Set recurring tasks on autopilot and see exactly what\u2019s running at a glance.",
      },
      {
        icon: ShieldCheck,
        title: "Team dashboards + controls",
        body: "When your team grows, keep everyone aligned with shared dashboards and permission controls.",
      },
    ],
  },
];

const weekCalendar = [
  {
    day: "Mon",
    title: "Booking + CRM",
    lines: [
      "Booking Agent drafts 8 venue follow-ups",
      "Email Agent personalizes 5 replies",
      "Manager Agent queues approvals",
    ],
  },
  {
    day: "Wed",
    title: "Marketing + Press",
    lines: [
      "Marketing Agent preps release kit",
      "Publicist Agent drafts 3 pitches",
      "Email Agent schedules newsletter",
    ],
  },
  {
    day: "Fri",
    title: "Momentum + Next steps",
    lines: [
      "Manager Agent highlights blockers",
      "Next-actions list is ready",
      "Weekly recap generated",
    ],
  },
];

const crmFlows = [
  "Venue contacts spread across email, texts, and DMs",
  "Tour ideas buried in notes and spreadsheets",
  "Ticket numbers coming from different platforms",
  "No clear picture of what shows actually earn",
  "Momentum getting lost between gigs",
];

const savingsScenarios = [
  { label: "Small room", show: "$900 show", fee: "$135 fee", savings: "$111 saved vs $24" },
  { label: "Mid club", show: "$2,500 show", fee: "$375 fee", savings: "$351 saved vs $24" },
  { label: "Good night", show: "$5,000 show", fee: "$750 fee", savings: "$726 saved vs $24" },
];

const pricingPlans = [
  {
    title: "Standard",
    price: "$24",
    cadence: "/mo",
    highlight: "Includes 1 seat · up to 4 total",
    seatPrice: "$14 / additional seat",
  },
  {
    title: "Pro",
    price: "$44",
    cadence: "/mo",
    highlight: "Includes 1 seat · up to 10 total",
    seatPrice: "$19 / additional seat",
  },
  {
    title: "Agency",
    price: "$99",
    cadence: "/mo",
    highlight: "Includes 5 seats · up to 20 total",
    seatPrice: "$19 / additional seat",
  },
  {
    title: "Enterprise",
    price: "Custom",
    cadence: "",
    highlight: "Contact us for enterprise pricing",
    seatPrice: "Custom seats + support",
  },
];

const faqs = [
  {
    q: "Is this only for independent musicians?",
    a: "No. ArtistAscend is built for solo artists and small teams, and it scales to agencies, labels, and festival talent departments.",
  },
  {
    q: "Can I keep my current booking workflow?",
    a: "Yes. Import your contacts, track leads, and layer in agent workflows only when you\u2019re ready.",
  },
  {
    q: "How does the CRM avoid duplicates?",
    a: "ArtistAscend checks email, company, and location before creating new records.",
  },
  {
    q: "What makes the agents different?",
    a: "They work inside your real data, propose actions, and wait for approvals so you stay in control.",
  },
  {
    q: "When can I get access?",
    a: "Enter your email above and you\u2019ll get instant access to start setting up your workspace.",
  },
];

const researchInsights = [
  {
    title: "Everything in one place",
    body: "We need booking, marketing, and tour ops in one system so the team doesn\u2019t lose momentum between tools.",
  },
  {
    title: "Automation with guardrails",
    body: "We want automation, but we still need human approvals before anything goes out to venues or press.",
  },
  {
    title: "Clarity across the roster",
    body: "Our talent team wants a live view of every artist pipeline without rebuilding spreadsheets each week.",
  },
];

const CONFETTI_COLORS = ["#e0c070", "#2c7b6a", "#90a0b0", "#d09b63", "#8090b0"];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type SignupStatus = "idle" | "loading" | "active" | "waitlisted" | "error";

export default function HelloLanding() {
  const [signupEmail, setSignupEmail] = useState("");
  const [signupStatus, setSignupStatus] = useState<SignupStatus>("idle");
  const [signupError, setSignupError] = useState("");
  const [waitlistPosition, setWaitlistPosition] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [signupSource, setSignupSource] = useState<string | null>(null);
  const [savingsIndex, setSavingsIndex] = useState(1);
  const [savingsAnimating, setSavingsAnimating] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("launch-dark-mode");
    if (stored !== null) return stored === "true";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const [cookieConsent, setCookieConsent] = useState<"accepted" | "declined" | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem("cookie-consent");
    return stored === "accepted" || stored === "declined" ? stored : null;
  });

  /* Count-up values for mock dashboard */
  const showsBooked = useCountUp(12, 1400);
  const activeLeads = useCountUp(48, 1600);
  const discovery = useCountUp(18, 1200);
  const outreach = useCountUp(21, 1300);
  const negotiation = useCountUp(7, 1000);
  const confirmed = useCountUp(2, 800);

  /* Dark mode: track system preference changes */
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (localStorage.getItem("launch-dark-mode") === null) {
        setDarkMode(e.matches);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("launch-dark-mode", String(next));
      return next;
    });
  };

  const handleCookieAccept = () => {
    localStorage.setItem("cookie-consent", "accepted");
    setCookieConsent("accepted");
  };

  const handleCookieDecline = () => {
    localStorage.setItem("cookie-consent", "declined");
    setCookieConsent("declined");
  };

  /* Capture UTM/referral source from Facebook or other ad campaigns */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source") || params.get("ref") || null;
    if (source) setSignupSource(source);
  }, []);

  /* Facebook Pixel — PageView on mount, consent-gated */
  useEffect(() => {
    if (!FB_PIXEL_ID || cookieConsent !== "accepted") return;
    initFBPixel();
    window.fbq?.("init", FB_PIXEL_ID);
    fbTrack("PageView");
  }, [cookieConsent]);

  /* SEO meta tags */
  useEffect(() => {
    const title = "ArtistAscend | Management + Booking for Modern Musicians";
    const description = "ArtistAscend is the management stack for musicians and teams. Book shows, manage CRM, and launch campaigns with human-approved agent workflows.";
    const url = "https://artistascend.app/hello";
    const image = "https://artistascend.app/og-artistascend.png";

    if (!document?.head) {
      return;
    }

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      if (!document.head) return;
      let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      if (!document.head) return;
      let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", url, true);
    setMeta("og:image", image, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
    setLink("canonical", url);
  }, []);

  const handleSignup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = signupEmail.trim();
    if (!email) {
      setSignupStatus("error");
      setSignupError("Enter a valid email to continue.");
      const input = event.currentTarget?.querySelector("input[type='email']") as HTMLInputElement | null;
      input?.focus();
      return;
    }
    setSignupStatus("loading");
    setSignupError("");
    // Track intent the moment they submit — valuable signal for FB optimisation
    fbTrack("Lead", { content_name: "signup_form" });
    try {
      const res = await fetch("/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, signup_source: signupSource }),
      });
      let data: any = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (!res.ok) {
        setSignupStatus("error");
        setSignupError(data?.detail || "Something went wrong.");
        const input = event.currentTarget?.querySelector("input[type='email']") as HTMLInputElement | null;
        input?.focus();
        return;
      }
      if (data?.status === "waitlisted") {
        setSignupStatus("waitlisted");
        setWaitlistPosition(data?.waitlist_position || 0);
      } else {
        if (!data?.access_token) {
          setSignupStatus("error");
          setSignupError("We could not complete signup. Please try again.");
          return;
        }
        setSignupStatus("active");
        localStorage.setItem("auth_token", data.access_token);
        localStorage.setItem("americana:agents:token", data.access_token);
        // Store a minimal profile so the billing gate in OnboardingFlow can evaluate
        // whether to redirect to billing before onboarding proceeds.
        localStorage.setItem(
          "americana:agents:profile",
          JSON.stringify({
            is_on_trial: true,
            trial_expired: false,
            signup_source: signupSource,
            subscription_status: null,
            billing_exempt: false,
            user: { is_owner: false },
          }),
        );
        setRuntimeAuthToken(data.access_token);
        // Re-init with Advanced Matching (email) for better attribution, then fire conversion
        fbInitWithEmail(email);
        fbTrack("CompleteRegistration", { value: 0, currency: "USD", status: true });
        setTimeout(() => {
          window.location.href = `/signup/account?email=${encodeURIComponent(email)}`;
        }, 1500);
      }
    } catch {
      setSignupStatus("error");
      setSignupError("Network error. Please try again.");
      const input = event.currentTarget?.querySelector("input[type='email']") as HTMLInputElement | null;
      input?.focus();
    }
  };

  const handleSavingsTab = (index: number) => {
    if (index === savingsIndex) return;
    setSavingsAnimating(true);
    setSavingsIndex(index);
    setTimeout(() => setSavingsAnimating(false), 400);
  };

  const signupForm = (id?: string) => {
    const formId = id || "hero-form";
    const inputId = `${formId}-email`;
    const errorId = `${formId}-error`;
    const statusId = `${formId}-status`;

    if (signupStatus === "active") {
      return (
        <div className="launch-success-card" id={id}>
          <div className="launch-confetti" aria-hidden="true">
            {Array.from({ length: 20 }).map((_, i) => (
              <span
                key={i}
                className="launch-confetti-piece"
                style={{
                  left: `${5 + i * 4.5}%`,
                  animationDelay: `${i * 0.08}s`,
                  backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                }}
              />
            ))}
          </div>
          <CheckCircle size={48} style={{ color: "#2c7b6a", marginBottom: 12 }} />
          <p style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: 8 }}>You&rsquo;re in!</p>
          <p style={{ color: darkMode ? "#a0aab3" : "#4a5560" }}>Redirecting to setup&hellip;</p>
          <div className="launch-progress-steps">
            <div className="launch-progress-step launch-progress-step--active">1</div>
            <div className="launch-progress-step-line" />
            <div className="launch-progress-step">2</div>
            <div className="launch-progress-step-line" />
            <div className="launch-progress-step">3</div>
            <div className="launch-progress-step-line" />
            <div className="launch-progress-step">4</div>
          </div>
          <p className="launch-progress-label">Step 1 of 4 &mdash; Account created</p>
          <a
            className="launch-btn"
            href={`/signup/account?email=${encodeURIComponent(signupEmail)}`}
            style={{ marginTop: 16 }}
          >
            Continue to setup
          </a>
        </div>
      );
    }
    if (signupStatus === "waitlisted") {
      return (
        <div className="launch-card" id={id} style={{ textAlign: "center", padding: "32px 24px" }}>
          <Clock size={48} style={{ color: "#8a5b2d", marginBottom: 12 }} />
          <p style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: 8 }}>You&rsquo;re on the list</p>
          {waitlistPosition > 0 && (
            <p style={{ color: darkMode ? "#a0aab3" : "#4a5560", marginBottom: 8 }}>Position #{waitlistPosition}</p>
          )}
          <p style={{ color: darkMode ? "#a0aab3" : "#4a5560" }}>We&rsquo;ll email you at <strong>{signupEmail}</strong> when a spot opens.</p>
          <button
            className="launch-btn launch-btn--ghost"
            type="button"
            style={{ marginTop: 16 }}
            onClick={() => {
              setSignupStatus("idle");
              setSignupError("");
              setWaitlistPosition(0);
              setSignupEmail("");
            }}
          >
            Use a different email
          </button>
        </div>
      );
    }
    return (
      <div className="launch-hero-form-wrap" id={id}>
        <form className="launch-hero-form" onSubmit={handleSignup}>
          <label htmlFor={inputId} className="launch-visually-hidden">Email address</label>
          <input
            id={inputId}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@yourband.com"
            value={signupEmail}
            onChange={(event) => {
              setSignupEmail(event.target.value);
              if (signupStatus === "error") {
                setSignupStatus("idle");
                setSignupError("");
              }
            }}
            required
            disabled={signupStatus === "loading"}
            aria-invalid={signupStatus === "error" && !!signupError}
            aria-describedby={signupStatus === "error" ? errorId : statusId}
          />
          <button className="launch-btn" type="submit" disabled={signupStatus === "loading"}>
            {signupStatus === "loading" ? "Creating account\u2026" : signupSource ? "Start Free Trial" : "Sign Up Free for 30 Days"}
          </button>
          <div className="launch-form-status" id={statusId} role="status" aria-live="polite">
            {signupStatus === "loading" ? "Submitting..." : ""}
          </div>
          {signupStatus === "error" && signupError && (
            <p className="launch-form-error" id={errorId} role="alert">{signupError}</p>
          )}
        </form>
        {signupStatus === "error" && signupError?.includes("already exists") && (
          <p className="launch-form-microcopy">
            Already have an account? <a href={SIGN_IN_URL}>Sign in</a>
          </p>
        )}
        <p className="launch-form-microcopy">
          {signupSource ? "30-day free trial · Cancel anytime · No credit card required" : "30-day free trial · No credit card required"}
        </p>
      </div>
    );
  };

  return (
    <div className={`launch-root launch-animate${darkMode ? " launch-dark" : ""}`}>
      {/* Facebook Pixel noscript fallback — only rendered after consent */}
      {FB_PIXEL_ID && cookieConsent === "accepted" && (
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
            alt=""
          />
        </noscript>
      )}

      {/* Cookie consent banner */}
      {cookieConsent === null && (
        <CookieBanner
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
          darkMode={darkMode}
        />
      )}

      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="launch-orb launch-orb--one" aria-hidden="true" />
      <div className="launch-orb launch-orb--two" aria-hidden="true" />

      {/* Header */}
      <header className="launch-shell">
        <div className="launch-header">
          <div className="launch-logo">
            <img className="launch-logo-mark" src={favicon} alt="ArtistAscend icon" />
            <span className="launch-logo-text">ArtistAscend</span>
          </div>
          <nav className="launch-nav">
            <a href="#features">Features</a>
            <a href="#agents">Agents</a>
            <a href="#crm">CRM</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="https://artistascend.app/docs">Docs</a>
          </nav>
          <button
            className="launch-nav-toggle"
            type="button"
            onClick={() => setNavOpen((prev) => !prev)}
            aria-expanded={navOpen}
            aria-controls="launch-mobile-nav"
          >
            Menu
          </button>
          <div className="launch-header-cta">
            <button
              className="launch-dark-toggle"
              type="button"
              onClick={toggleDarkMode}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <a className="launch-btn launch-btn--ghost" href={SIGN_IN_URL}>Sign In</a>
          </div>
        </div>
        <div className={`launch-mobile-nav ${navOpen ? "is-open" : ""}`} id="launch-mobile-nav">
          <a href="#features" onClick={() => setNavOpen(false)}>Features</a>
          <a href="#agents" onClick={() => setNavOpen(false)}>Agents</a>
          <a href="#crm" onClick={() => setNavOpen(false)}>CRM</a>
          <a href="#pricing" onClick={() => setNavOpen(false)}>Pricing</a>
          <a href="#faq" onClick={() => setNavOpen(false)}>FAQ</a>
          <a href="https://artistascend.app/docs" onClick={() => setNavOpen(false)}>Docs</a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="launch-shell launch-hero">
          <div className="launch-hero-copy">
            <p className="launch-eyebrow">
              {signupSource
                ? "30-day free trial · No credit card required"
                : "Build your music career with less chaos."}
            </p>
            <h1 className="launch-display">
              Turn touring into a <span className="launch-gradient-tech">repeatable</span> system.
            </h1>
            <p className="launch-lead">
              {signupSource
                ? "Try ArtistAscend free for 30 days. Book shows, manage your pipeline, and track earnings — all in one workspace built for working musicians."
                : "Everything you need to book, route, and track earnings in one calm workspace. Keep moving forward without giving away percentages."}
            </p>
            {signupForm("hero-form")}
          </div>
          <div className="launch-hero-visual">
            <div className="launch-mock">
              <div className="launch-mock-header">
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="launch-mock-live-dot" aria-hidden="true" />
                  ArtistAscend Live
                </span>
                <span className="launch-mock-pill">Pipeline Sync</span>
              </div>
              <div className="launch-mock-body">
                <div className="launch-metric">
                  <p className="launch-metric-label">Shows Booked</p>
                  <p className="launch-metric-value">{showsBooked}</p>
                  <p className="launch-metric-note">+3 this month</p>
                </div>
                <div className="launch-metric">
                  <p className="launch-metric-label">Active Leads</p>
                  <p className="launch-metric-value">{activeLeads}</p>
                  <p className="launch-metric-note launch-metric-note--pulse">8 awaiting reply</p>
                </div>
                <div className="launch-pipeline">
                  <div className="launch-pipeline-row">
                    <span>Discovery</span>
                    <span>{discovery}</span>
                  </div>
                  <div className="launch-pipeline-row">
                    <span>Outreach</span>
                    <span>{outreach}</span>
                  </div>
                  <div className="launch-pipeline-row">
                    <span>Negotiation</span>
                    <span>{negotiation}</span>
                  </div>
                  <div className="launch-pipeline-row">
                    <span>Confirmed</span>
                    <span>{confirmed}</span>
                  </div>
                </div>
                <div className="launch-mock-footer">
                  <div>
                    <p className="launch-mock-label">Next action</p>
                    <p className="launch-mock-value">
                      Approve 3 booking drafts
                      <span className="launch-typing-indicator" aria-hidden="true">
                        <span className="launch-typing-dot" />
                        <span className="launch-typing-dot" />
                        <span className="launch-typing-dot" />
                      </span>
                    </p>
                  </div>
                  <button className="launch-mock-action" type="button">Review now</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Bar */}
        <div className="launch-social-proof">
          <Music size={16} />
          <span>Built for indie artists, managers &amp; booking teams</span>
          <span className="launch-social-proof-sep" />
          <Headphones size={16} />
          <span>From solo acts to full rosters</span>
          <span className="launch-social-proof-sep" />
          <Mic size={16} />
          <span>No percentage cuts</span>
        </div>

        {/* CRM / Problem Section */}
        <section className="launch-shell launch-section launch-split" id="crm">
          <div>
            <h2 className="launch-section-title">Most artists aren&rsquo;t short on talent. They&rsquo;re short on clarity.</h2>
            <p className="launch-section-body">
              When everything lives somewhere different, progress stalls. ArtistAscend brings the work of running a band
              into one place &mdash; including the part most artists never see clearly: the money.
            </p>
            <ul className="launch-list">
              {crmFlows.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="launch-highlight">
            <span className="launch-badge">Human-in-the-loop</span>
            <h3>Your approvals, always</h3>
            <p>
              Agents surface drafts and recommendations, but you approve every outreach, update, and booking decision.
            </p>
            <div className="launch-highlight-row">
              <span><CheckCircle size={13} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 7, opacity: 0.65 }} />Approvals stay in your hands</span>
              <span><CheckCircle size={13} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 7, opacity: 0.65 }} />AI enrichment stays transparent</span>
              <span><CheckCircle size={13} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 7, opacity: 0.65 }} />Every change is traceable</span>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="launch-shell launch-section" id="features">
          <div className="launch-section-head">
            <h2 className="launch-section-title">One calm workspace for your band.</h2>
            <p className="launch-section-body">
              Track venues, plan tours, manage revenue, and keep the band aligned without jumping between inboxes,
              spreadsheets, and platforms.
            </p>
          </div>
          <div className="launch-feature-groups">
            {featureGroups.map((group) => (
              <div className={`launch-feature-group launch-feature-group--${group.slug}`} key={group.title}>
                <div className="launch-feature-header">
                  <h3 className="launch-feature-title">{group.title}</h3>
                  <div className="launch-feature-rule" />
                </div>
                <div className="launch-grid">
                  {group.items.map((item) => (
                    <div className="launch-card launch-card--glass launch-card--flare" key={item.title}>
                      <item.icon className="launch-card-icon" aria-hidden="true" />
                      <h4 className="launch-card-heading">{item.title}</h4>
                      <p>{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Weekly Calendar */}
        <section className="launch-shell launch-section" id="agents">
          <div className="launch-section-head">
            <h2 className="launch-section-title">A week in the life with ArtistAscend.</h2>
            <p className="launch-section-body">
              Agents help you follow up consistently, route smarter tours, and keep everyone aligned. You approve every
              action.
            </p>
          </div>
          <div className="launch-calendar-timeline">
            {weekCalendar.map((item, idx) => (
              <React.Fragment key={item.day}>
                {idx > 0 && (
                  <div className="launch-calendar-gap">
                    <span className="launch-calendar-gap-day">
                      {idx === 1 ? "Tue" : "Thu"}
                    </span>
                    <span className="launch-calendar-gap-line" />
                  </div>
                )}
                <div className="launch-calendar-card">
                  <div className="launch-calendar-header">
                    <span className="launch-calendar-day">{item.day}</span>
                  </div>
                  <h3>{item.title}</h3>
                  <ul>
                    {item.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* Pricing — Calculator Only */}
        <section className="launch-shell launch-section" id="pricing">
          <div className="launch-section-head">
            <h2 className="launch-section-title">Keep more of what you earn.</h2>
            <p className="launch-section-body">
              Flat monthly pricing replaces percentage-based fees. No surprises, no cuts from your guarantee.
            </p>
          </div>
          <div className="launch-grid" style={{ marginBottom: 28 }}>
            {pricingPlans.map((plan) => (
              <div className={`launch-card launch-card--glass${plan.title === "Pro" ? " launch-card--featured" : ""}`} key={plan.title}>
                {plan.title === "Pro" && <span className="launch-card-featured-badge">Most Popular</span>}
                <p className="launch-card-heading" style={{ marginBottom: 6 }}>{plan.title}</p>
                <p style={{ fontSize: "2rem", fontWeight: 700, margin: "4px 0" }}>
                  {plan.price} <span style={{ fontSize: "1rem", fontWeight: 500 }}>{plan.cadence}</span>
                </p>
                <p style={{ color: darkMode ? "#a0aab3" : "#4a5560", marginBottom: 10 }}>{plan.highlight}</p>
                <p style={{ fontSize: "0.95rem" }}>{plan.seatPrice}</p>
              </div>
            ))}
          </div>
          <p className="launch-form-microcopy" style={{ marginTop: -8 }}>
            Need more seats? Contact us for enterprise pricing.
          </p>
          <div className="launch-pricing-wrap">
            <div className="launch-pricing-impact">
              <div className="launch-impact-header">
                <h3>See what you keep</h3>
                <div className="launch-impact-tabs">
                  {savingsScenarios.map((scenario, index) => (
                    <button
                      key={scenario.label}
                      type="button"
                      className={`launch-impact-tab ${index === savingsIndex ? "is-active" : ""}`}
                      onClick={() => handleSavingsTab(index)}
                    >
                      {scenario.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={`launch-impact-cards${savingsAnimating ? " launch-impact-cards--enter" : ""}`}>
                <div className="launch-impact-card">
                  <p className="launch-impact-label">Traditional model</p>
                  <p className="launch-impact-main">{savingsScenarios[savingsIndex].show}</p>
                  <p className="launch-impact-sub">{savingsScenarios[savingsIndex].fee} at 15%</p>
                </div>
                <div className="launch-impact-card launch-impact-card--hero">
                  <p className="launch-impact-label">ArtistAscend</p>
                  <p className="launch-impact-main launch-impact-main--lg">$24 / mo</p>
                  <p className="launch-impact-sub" key={`savings-${savingsIndex}`}>
                    {savingsScenarios[savingsIndex].savings}
                  </p>
                </div>
              </div>
            </div>
            <div className="launch-time-saved">
              <Clock size={20} style={{ opacity: 0.6 }} />
              <p>
                Most artists spend <strong>hours</strong> drafting and sending booking emails one by one.
                With ArtistAscend, draft and send in <strong>minutes</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Research Insights (was Testimonials) */}
        <section className="launch-shell launch-section" id="testimonials">
          <div className="launch-section-head">
            <h2 className="launch-section-title">What artists told us they need</h2>
            <p className="launch-section-body">
              These are real problems we heard from touring musicians, managers, and booking teams.
            </p>
          </div>
          <div className="launch-grid launch-grid--quotes">
            {researchInsights.map((item, index) => (
              <div className="launch-card launch-card--glass" key={`${item.title}-${index}`}>
                <h4 className="launch-card-heading">{item.title}</h4>
                <p className="launch-quote-body">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="launch-shell launch-section" id="faq">
          <div className="launch-section-head">
            <h2 className="launch-section-title">Frequently asked questions</h2>
            <p className="launch-section-body">You don&rsquo;t need to be industry-ready. You just need clarity.</p>
          </div>
          <div className="launch-accordion">
            {faqs.map((item) => (
              <details className="launch-accordion-item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="launch-shell launch-section launch-cta" id="waitlist">
          <div className="launch-section-head">
            <h2 className="launch-section-title">Start building the booking engine you wish you had.</h2>
            <p className="launch-section-body">
              Get set up in minutes with a guided path tailored to how you manage artists.
            </p>
            <ul className="launch-cta-list">
              <li><CheckCircle size={14} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8, color: "#2c7b6a" }} />Start simple and keep momentum.</li>
              <li><CheckCircle size={14} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8, color: "#2c7b6a" }} />See what each show actually earned.</li>
              <li><CheckCircle size={14} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8, color: "#2c7b6a" }} />Invite your band team when you&rsquo;re ready.</li>
            </ul>
          </div>
          <div className="launch-cta-box">
            <div>
              <p className="launch-cta-title">Get started free</p>
              <p className="launch-cta-body">
                Create your account now and start setting up your workspace.
              </p>
            </div>
            {signupForm("waitlist-form")}
            <a className="launch-form-link" href={SIGN_IN_URL}>Already have an account? Sign in</a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="launch-shell launch-footer">
        <div className="launch-footer-brand">
          <img className="launch-logo-mark" src={favicon} alt="ArtistAscend" />
          <div>
            <p className="launch-footer-title">ArtistAscend</p>
            <p className="launch-footer-body">The management stack for modern music teams.</p>
          </div>
        </div>
        <div className="launch-footer-links">
          <a href="#features">Features</a>
          <a href="#agents">Agents</a>
          <a href="#crm">CRM</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="launch-footer-links">
          <a href="https://artistascend.app/security">Security</a>
          <a href="https://artistascend.app/terms">Terms of Service</a>
          <a href="https://artistascend.app/privacy">Privacy Policy</a>
          <a href="https://artistascend.app/docs">Docs</a>
        </div>
        <p className="launch-footer-note">&copy; 2026 ArtistAscend. All rights reserved.</p>
      </footer>
    </div>
  );
}
